import { useCallback, useMemo } from 'react'
import SmartPalette from '../../../generic/smartPalette/SmartPalette'
import { useSmartTranslation } from '../../../generic/hooks/useSmartTranslation'
import useFrontEndConfig from '../../../generic/hooks/useFrontEndConfig/useFrontEndConfig'
import { formatPartReference } from '../../orders/partReferenceLabel'
import WorkflowResourceTypes from './WorkflowResourcesTypes'

const ResourceTypesBar = ({
  parts,
  rawMaterials,
  workflowResources,
  editMode,
  hasWorkflowResources
}) => {
  const { t_ } = useSmartTranslation()
  // Historia 125 (MIC-4261), delta del PO: the part reference reads in the installation's format,
  // through the same helper the request form uses. Painting and resolving with ONE format is what
  // keeps a chosen option findable (E4).
  const [frontEndConfig] = useFrontEndConfig()
  const partReferenceDisplayFormat = frontEndConfig?.partReferenceDisplayFormat

  const sections = useMemo(() => {
    if (hasWorkflowResources) {
      return [
        {
          id: 'BOM',
          type: 'BOM',
          title: t_('BOM'),
          items: workflowResources,
          defaultExpanded: true,
          customRender: (section, items, getDragProperties) => (
            <WorkflowResourceTypes
              key='bom-stable-tree'
              section={section}
              items={items}
              getDragProperties={getDragProperties}
            />
          )
        }
      ]
    }

    return [
      { id: 'PART_REFERENCE', type: 'PART_REFERENCE', title: t_('Part reference'), items: parts },
      { id: 'RAW_MATERIAL_REFERENCE', type: 'RAW_MATERIAL_REFERENCE', title: t_('Raw material reference'), items: rawMaterials }
    ]
  }, [parts, rawMaterials, t_, hasWorkflowResources, workflowResources])

  const getItemChild = (item, allItems) => {
    const items = allItems
    items.push(item)

    if (!item.children || item.children.length === 0) {
      return items
    }

    item.children.forEach(entry => {
      items.concat(getItemChild(entry, items))
    })

    return items
  }

  const getResourceTypeCode = (entry) => {
    const resourceType = entry.resourceTypeName || entry.resourceType || entry.type
    return typeof resourceType === 'string' ? resourceType : resourceType?.code
  }

  const getResourcesToDrag = (item) => {
    const dragProps = {}
    const items = getItemChild(item, [])

    dragProps['application/reactflow/itemQuantity'] = items.length
    items.forEach((entry, index) => {
      dragProps['application/reactflow/code_' + index] = entry.resource.code
      dragProps['application/reactflow/resourceId_' + index] = entry.resource.id
      dragProps['application/reactflow/resourceType_' + index] = getResourceTypeCode(entry)
      dragProps['application/reactflow/name_' + index] = entry.resource.name
      dragProps['application/reactflow/workflowResourceUUID_' + index] = entry.uuid
      dragProps['application/reactflow/engItem_' + index] = entry.engItem
      dragProps['application/reactflow/engRevision_' + index] = entry.engRevision
      dragProps['application/reactflow/position_' + index] = entry.position
      dragProps['application/reactflow/revision_' + index] = entry.revision
      dragProps['application/reactflow/quantity_' + index] = entry.quantity
      dragProps['application/reactflow/defaultWorkflow_' + index] = entry.resource.defaultWorkflow
      dragProps['application/reactflow/category_' + index] = entry.category
      dragProps['application/reactflow/componentType_' + index] = entry.componentType
      dragProps['application/reactflow/fractionable_' + index] = entry.resource?.fractionable ?? entry.fractionable
      dragProps['application/reactflow/size_' + index] = entry.resource?.size ?? entry.size
      dragProps['application/reactflow/unitCode_' + index] = entry.resource?.unitCode ?? entry.resource?.unit?.code ?? entry.unitCode ?? entry.unit?.code
      dragProps['application/reactflow/extendedColumn1_' + index] = entry.extended_column1
      dragProps['application/reactflow/extendedColumn2_' + index] = entry.extended_column2
      dragProps['application/reactflow/extendedColumn3_' + index] = entry.extended_column3
      dragProps['application/reactflow/extendedColumn4_' + index] = entry.extended_column4
      dragProps['application/reactflow/extendedColumn5_' + index] = entry.extended_column5
      dragProps['application/reactflow/extendedColumn6_' + index] = entry.extended_column6
      dragProps['application/reactflow/extendedColumn7_' + index] = entry.extended_column7
      dragProps['application/reactflow/extendedColumn8_' + index] = entry.extended_column8
      dragProps['application/reactflow/extendedColumn9_' + index] = entry.extended_column9
      dragProps['application/reactflow/extendedColumn10_' + index] = entry.extended_column10
      dragProps['application/reactflow/extendedColumn11_' + index] = entry.extended_column11
      dragProps['application/reactflow/extendedColumn12_' + index] = entry.extended_column12
    })
    return dragProps
  }

  const getDragProperties = (section, item) => {
    if (section.id === 'BOM') {
      return getResourcesToDrag(item)
    }

    // if (section.id === 'RAW_MATERIAL_REFERENCE' || section.id === 'PART_REFERENCE') {
    const props = {
      'application/reactflow/itemQuantity': 1,
      'application/reactflow/code_0': item.code,
      'application/reactflow/resourceId_0': item.id,
      'application/reactflow/resourceType_0': section.id,
      'application/reactflow/name_0': item.name
    }

    if (section.id === 'PART_REFERENCE' && item.defaultWorkflow) {
      props['application/reactflow/defaultWorkflow'] = item.defaultWorkflow
    }

    if (section.id === 'RAW_MATERIAL_REFERENCE') {
      props['application/reactflow/fractionable_0'] = item.fractionable
      props['application/reactflow/size_0'] = item.size
      props['application/reactflow/unitCode_0'] = item.unitCode ?? item.unit?.code
    }

    return props
  }

  // Only a PART REFERENCE follows the installation's display format: the palette also lists raw
  // materials and BOM entries, whose label is not what historia 125 configures.
  const getLabel = useCallback((section, item) => {
    if (section?.id === 'PART_REFERENCE') {
      return formatPartReference(item, partReferenceDisplayFormat)
    }
    return item?.name + ' (' + item?.code + ')'
  }, [partReferenceDisplayFormat])

  return (
    <SmartPalette
      pageable
      itemsPerPage={5}
      sections={sections}
      getDragProperties={(section, item) => getDragProperties(section, item)}
      isItemDisabled={() => !editMode}
      getLabel={getLabel}
      searchByCodeName
    />
  )
}

export default ResourceTypesBar
